/**
 * Error handling helpers for API errors. Use with ServerError and error codes from config/errorCodes.
 */

import { isAxiosError } from "axios";
import { ERROR_CODE_TOAST_MESSAGES, GENERIC_SERVER_ERROR_MESSAGE } from "config/errorCodes";
import { ServerError } from "./genericUtilityFunctions";

export interface ErrorLike {
  code?: string;
  message?: string;
}

/**
 * Returns the message to show in a toast for an API error.
 * Uses ERROR_CODE_TOAST_MESSAGES when we have a preferred UX message for the code,
 * otherwise the API message, otherwise the generic server error message.
 */
export function getToastMessageForError(error: ErrorLike): string {
  if (!error) return GENERIC_SERVER_ERROR_MESSAGE;
  const codeMessage =
    error.code && error.code in ERROR_CODE_TOAST_MESSAGES
      ? ERROR_CODE_TOAST_MESSAGES[error.code as keyof typeof ERROR_CODE_TOAST_MESSAGES]
      : undefined;
  return codeMessage ?? error.message ?? GENERIC_SERVER_ERROR_MESSAGE;
}

const hasBodyMessage = (data: unknown): boolean => {
  if (!data || typeof data !== "object") return false;
  const message = (data as { message?: unknown }).message;
  if (typeof message === "string") return message.trim().length > 0;
  return Array.isArray(message) && message.length > 0;
};

/**
 * Toast text for a failed request that starts work (AI reruns and similar).
 * A 4xx response carries a safe, user-facing message from the API, so show it
 * (or our own text when we map the code). A 5xx, network failure, or anything
 * without an API message gets `fallback`, so raw server text never reaches users.
 */
export function getApiErrorToastMessage(error: unknown, fallback: string): string {
  let serverError: ServerError;
  if (error instanceof ServerError) {
    serverError = error;
  } else if (isAxiosError(error) && error.response) {
    if (!hasBodyMessage(error.response.data)) return fallback;
    serverError = ServerError.fromAxiosError(error);
  } else {
    return fallback;
  }

  const status = serverError.status_code;
  if (!(status >= 400 && status < 500)) return fallback;
  const message = getToastMessageForError(serverError);
  return message?.trim() ? message : fallback;
}
